import type { InstallState } from '@volley/crucible-ipc-types/ipc-channels';
import type { FoundryPreviewDevState } from '@volley/crucible/foundry/state-kernel.js';
import {
  PREVIEW_PHASE_GATE_MIN,
  formatPreviewPhaseGateMessage,
} from '@volley/crucible-ipc-types/preview-gate-constants';
import type { PreviewSessionOperation, PreviewSessionState } from '../hooks/usePreviewSession';

export type PreviewDevLoopKind =
  | 'idle'
  | 'checking'
  | 'install-needed'
  | 'installing'
  | 'starting'
  | 'ready'
  | 'restarting'
  | 'stopping'
  | 'blocked'
  | 'failed';

export type PreviewDevLoopTone = 'neutral' | 'active' | 'waiting' | 'success' | 'warning' | 'error';

export type PreviewDevLoopCta =
  | 'start-preview'
  | 'retry-install-and-start'
  | 'restart-preview'
  | 'retry-start-preview'
  | 'reload-preview'
  | 'send-error-to-chat';

export interface PreviewDevLoopState {
  kind: PreviewDevLoopKind;
  label: string;
  detail?: string;
  tone: PreviewDevLoopTone;
  primaryCta?: PreviewDevLoopCta;
  secondaryCtas: PreviewDevLoopCta[];
  disabledReason?: string;
  debugSource: string;
}

export interface PreviewDevLoopPhaseGate {
  currentPhase: number;
  phaseLabel: string;
}

export interface PreviewDevLoopInput {
  gameId: string | undefined;
  phaseGate?: PreviewDevLoopPhaseGate;
  installState: InstallState | null | undefined;
  installStateLoading: boolean;
  installRetrying: boolean;
  installRetryError?: string | null;
  previewSession: PreviewSessionState;
  foundryPreviewDev?: FoundryPreviewDevState | null;
}

export function derivePreviewDevLoopState(input: PreviewDevLoopInput): PreviewDevLoopState {
  if (!input.gameId) {
    return {
      kind: 'blocked',
      label: 'Preview unavailable',
      detail: 'Open a game before launching preview.',
      tone: 'neutral',
      secondaryCtas: [],
      disabledReason: 'Preview is unavailable until a game is loaded.',
      debugSource: 'game:none',
    };
  }

  if (input.phaseGate && input.phaseGate.currentPhase < PREVIEW_PHASE_GATE_MIN) {
    const detail = formatPreviewPhaseGateMessage(
      input.phaseGate.currentPhase,
      input.phaseGate.phaseLabel,
    );
    return {
      kind: 'blocked',
      label: 'Preview locked',
      detail,
      tone: 'neutral',
      secondaryCtas: [],
      disabledReason: detail,
      debugSource: `phase-gate:${input.phaseGate.currentPhase}`,
    };
  }

  if (input.installRetrying) {
    return {
      kind: 'installing',
      label: 'Installing dependencies',
      detail: 'Foundry is retrying the game dependency install.',
      tone: 'active',
      primaryCta: 'retry-install-and-start',
      secondaryCtas: [],
      disabledReason: 'Install retry is already running.',
      debugSource: 'install:retrying',
    };
  }

  if (input.installState?.status === 'installing') {
    return {
      kind: 'installing',
      label: 'Installing dependencies',
      detail: 'Foundry is installing game dependencies.',
      tone: 'active',
      primaryCta: 'retry-install-and-start',
      secondaryCtas: [],
      disabledReason: 'Install is already running.',
      debugSource: 'install:installing',
    };
  }

  if (input.installState?.status === 'failed') {
    const reason =
      input.installRetryError ?? input.installState.reason ?? 'Dependency install failed.';
    return {
      kind: 'install-needed',
      label: 'Dependencies not installed',
      detail: reason,
      tone: 'warning',
      primaryCta: 'retry-install-and-start',
      secondaryCtas: [],
      debugSource: 'install:failed',
    };
  }

  if (input.previewSession.status === 'busy') {
    return busyState(input.previewSession.operation);
  }

  if (input.previewSession.status === 'ready') {
    if (input.foundryPreviewDev && !input.foundryPreviewDev.running) {
      return staleKernelPreviewState(input.foundryPreviewDev, 'restart-preview');
    }
    return {
      kind: 'ready',
      label: 'Preview running',
      detail: 'Display and controller are connected to the dev server.',
      tone: 'success',
      primaryCta: 'restart-preview',
      secondaryCtas: ['reload-preview'],
      debugSource: `preview:ready:${input.previewSession.session.gameId}`,
    };
  }

  if (input.previewSession.status === 'error') {
    return {
      kind: 'failed',
      label: `${operationVerb(input.previewSession.operation)} failed`,
      detail: input.previewSession.error.message || 'Preview failed.',
      tone: 'error',
      primaryCta: errorPrimaryCta(input.previewSession.operation),
      secondaryCtas: ['send-error-to-chat'],
      debugSource: `preview:error:${input.previewSession.operation}`,
    };
  }

  if (input.installStateLoading) {
    return {
      kind: 'checking',
      label: 'Checking preview',
      detail: 'Foundry is checking install state before preview.',
      tone: 'neutral',
      secondaryCtas: [],
      disabledReason: 'Install state is still loading.',
      debugSource: 'install:loading',
    };
  }

  if (input.foundryPreviewDev?.status === 'stale') {
    return staleKernelPreviewState(input.foundryPreviewDev, 'start-preview');
  }

  if (input.foundryPreviewDev?.status === 'failed') {
    return {
      kind: 'failed',
      label: 'Preview failed',
      detail: input.foundryPreviewDev.reason || 'The previous preview session failed.',
      tone: 'error',
      primaryCta: 'retry-start-preview',
      secondaryCtas: ['send-error-to-chat'],
      debugSource: `kernel-preview:failed:${input.foundryPreviewDev.reason}`,
    };
  }

  return {
    kind: 'idle',
    label: 'Preview idle',
    detail: 'Start preview to launch display and controller.',
    tone: 'neutral',
    primaryCta: 'start-preview',
    secondaryCtas: [],
    debugSource: 'preview:idle',
  };
}

function staleKernelPreviewState(
  previewDev: FoundryPreviewDevState,
  primaryCta: 'start-preview' | 'restart-preview',
): PreviewDevLoopState {
  return {
    kind: primaryCta === 'restart-preview' ? 'failed' : 'idle',
    label: primaryCta === 'restart-preview' ? 'Preview stale' : 'Preview stopped',
    detail: `Foundry no longer sees a live preview session (${previewDev.reason}).`,
    tone: 'warning',
    primaryCta,
    secondaryCtas: [],
    debugSource: `kernel-preview:stale:${previewDev.reason}`,
  };
}

function busyState(operation: PreviewSessionOperation): PreviewDevLoopState {
  switch (operation) {
    case 'restarting':
      return {
        kind: 'restarting',
        label: 'Restarting preview',
        detail: 'Foundry is cycling the dev server and refreshing preview URLs.',
        tone: 'active',
        primaryCta: 'restart-preview',
        secondaryCtas: [],
        disabledReason: 'Preview restart is already running.',
        debugSource: 'preview:busy:restarting',
      };
    case 'stopping':
      return {
        kind: 'stopping',
        label: 'Stopping preview',
        detail: 'Foundry is stopping the dev server.',
        tone: 'active',
        secondaryCtas: [],
        disabledReason: 'Preview stop is already running.',
        debugSource: 'preview:busy:stopping',
      };
    case 'starting':
      return {
        kind: 'starting',
        label: 'Starting preview',
        detail: 'Foundry is spawning display, controller, and server processes.',
        tone: 'active',
        primaryCta: 'start-preview',
        secondaryCtas: [],
        disabledReason: 'Preview start is already running.',
        debugSource: 'preview:busy:starting',
      };
  }
}

function operationVerb(operation: PreviewSessionOperation): string {
  switch (operation) {
    case 'restarting':
      return 'Restart';
    case 'stopping':
      return 'Stop';
    case 'starting':
      return 'Start';
  }
}

function errorPrimaryCta(operation: PreviewSessionOperation): PreviewDevLoopCta | undefined {
  switch (operation) {
    case 'starting':
      return 'retry-start-preview';
    case 'restarting':
      return 'restart-preview';
    case 'stopping':
      return undefined;
  }
}
